import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { Building2, Plus, Settings, ExternalLink, Key, Shield, Activity, LogOut, User, Server, BarChart3, Archive, Trash2, Edit, Download, AlertTriangle, Clock, Users, FileText, CheckCircle, RotateCcw, MessageSquare } from 'lucide-react'
import PlatformAdminLogin from '../components/PlatformAdminLogin'
import PlatformAdminDashboard from '../components/PlatformAdminDashboard'
import PlatformKeysManager from '../components/PlatformKeysManager'
import PlatformAnalytics from '../components/PlatformAnalytics'
import AuditLogViewer from '../components/AuditLogViewer'
import SystemHealthMonitor from '../components/SystemHealthMonitor'
import BulkRedeployPanel from '../components/BulkRedeployPanel'
import PlatformSettings from '../components/PlatformAdmin'
import AIPromptAnalytics from '../components/AIPromptAnalytics'

// Platform Admin v2.0 - Archive, Recycle Bin, Metrics

interface Tenant {
  id: string
  name: string
  domain: string
  createdAt: string
  updatedAt: string
  status?: 'active' | 'pending' | 'archived'
  isDeleted?: boolean
  deletedAt?: string
  deploymentUrl?: string
  settings?: {
    primaryColor?: string
    logo?: string
    customDomain?: string
  }
  metrics?: {
    lastLoginAt?: string
    lastActivityAt?: string
    totalUsers?: number
    activeUsers?: number
    totalDocuments?: number
    totalRequests?: number
    ndaAcceptances?: number
  }
}

interface AuthUser {
  email: string
  name?: string
  provider: 'google' | 'github'
  sessionId: string
  expiresAt: string
}

type Tab = 'dashboard' | 'tenants' | 'archive' | 'recycle' | 'keys' | 'analytics' | 'audit' | 'ai-prompts' | 'health' | 'settings'

const PlatformAdmin = () => {
  const navigate = useNavigate()

  // Authentication state
  const [isAuthenticated, setIsAuthenticated] = useState<boolean | null>(null)
  const [user, setUser] = useState<AuthUser | null>(null)
  const [authLoading, setAuthLoading] = useState(true)

  // UI state
  const [activeTab, setActiveTab] = useState<Tab>('dashboard')
  const [tenants, setTenants] = useState<Tenant[]>([])
  const [archivedTenants, setArchivedTenants] = useState<Tenant[]>([])
  const [deletedTenants, setDeletedTenants] = useState<Tenant[]>([])
  const [loading, setLoading] = useState(false)
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [showUserMenu, setShowUserMenu] = useState(false)
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [showArchiveModal, setShowArchiveModal] = useState(false)
  const [showRestoreModal, setShowRestoreModal] = useState(false)
  const [selectedTenant, setSelectedTenant] = useState<Tenant | null>(null)
  const [deletionCertificate, setDeletionCertificate] = useState<string | null>(null)
  const [newTenant, setNewTenant] = useState({
    name: '',
    domain: ''
  })

  // Check authentication on mount
  useEffect(() => {
    checkAuth()
  }, [])

  // Load tenants when tenants tab is active
  useEffect(() => {
    if (activeTab === 'tenants' && isAuthenticated) {
      loadTenants()
    } else if (activeTab === 'archive' && isAuthenticated) {
      loadArchivedTenants()
    } else if (activeTab === 'recycle' && isAuthenticated) {
      loadDeletedTenants()
    }
  }, [activeTab, isAuthenticated])

  const checkAuth = async () => {
    setAuthLoading(true)
    try {
      const response = await fetch('/api/platform-admin/auth/validate', {
        credentials: 'include'
      })

      if (response.ok) {
        const data = await response.json()
        setIsAuthenticated(true)
        setUser(data.user)
      } else {
        setIsAuthenticated(false)
        setUser(null)
      }
    } catch (error) {
      console.error('Auth check failed:', error)
      setIsAuthenticated(false)
      setUser(null)
    } finally {
      setAuthLoading(false)
    }
  }

  const handleLogout = async () => {
    try {
      await fetch('/api/platform-admin/auth/logout', {
        method: 'POST',
        credentials: 'include'
      })

      setIsAuthenticated(false)
      setUser(null)
      setActiveTab('dashboard')

      // Clear any local state
      window.location.reload()
    } catch (error) {
      console.error('Logout failed:', error)
      alert('Failed to logout')
    }
  }

  const loadTenants = async () => {
    setLoading(true)
    try {
      const response = await fetch('/api/admin/tenants?status=active', {
        credentials: 'include'
      })

      if (response.ok) {
        const data = await response.json()
        setTenants(data)
      } else {
        console.error('Failed to load tenants:', response.status)
      }
    } catch (error) {
      console.error('Error loading tenants:', error)
    } finally {
      setLoading(false)
    }
  }

  const loadArchivedTenants = async () => {
    setLoading(true)
    try {
      const response = await fetch('/api/admin/tenants?status=archived', {
        credentials: 'include'
      })

      if (response.ok) {
        const data = await response.json()
        setArchivedTenants(data)
      } else {
        console.error('Failed to load archived tenants:', response.status)
      }
    } catch (error) {
      console.error('Error loading archived tenants:', error)
    } finally {
      setLoading(false)
    }
  }

  const loadDeletedTenants = async () => {
    setLoading(true)
    try {
      const response = await fetch('/api/admin/tenants?status=deleted', {
        credentials: 'include'
      })

      if (response.ok) {
        const data = await response.json()
        setDeletedTenants(data)
      } else {
        console.error('Failed to load deleted tenants:', response.status)
      }
    } catch (error) {
      console.error('Error loading deleted tenants:', error)
    } finally {
      setLoading(false)
    }
  }

  const createTenant = async () => {
    if (!newTenant.name || !newTenant.domain) {
      alert('Please fill in all fields')
      return
    }

    try {
      const response = await fetch('/api/admin/tenants', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(newTenant)
      })

      if (response.ok) {
        setShowCreateModal(false)
        setNewTenant({ name: '', domain: '' })
        loadTenants()
      } else {
        const error = await response.text()
        alert(`Failed to create tenant: ${error}`)
      }
    } catch (error: any) {
      alert(`Error creating tenant: ${error.message}`)
    }
  }

  const openTenantAdmin = (tenantId: string) => {
    navigate(`/platformadmin/tenant/${tenantId}`)
  }

  const hasSite = (tenant: Tenant): boolean => {
    // Tenant has a site if they have a custom domain, deployment URL, or are active
    return !!(tenant.settings?.customDomain || tenant.deploymentUrl || tenant.status === 'active')
  }

  const openLiveSite = (tenant: Tenant) => {
    if (!hasSite(tenant)) {
      return // Don't open if no site exists
    }

    if (tenant.settings?.customDomain) {
      window.open(`https://${tenant.settings.customDomain}`, '_blank')
    } else if (tenant.deploymentUrl) {
      window.open(tenant.deploymentUrl, '_blank')
    } else {
      // Fallback: construct URL from tenant ID
      const currentOrigin = window.location.origin
      window.open(`${currentOrigin}/?tenant=${tenant.id}`, '_blank')
    }
  }

  const handleArchiveTenant = (tenant: Tenant) => {
    setSelectedTenant(tenant)
    setShowArchiveModal(true)
  }

  const archiveTenant = async () => {
    if (!selectedTenant) return

    try {
      const response = await fetch(`/api/admin/tenants/${selectedTenant.id}/archive`, {
        method: 'POST',
        credentials: 'include'
      })

      if (response.ok) {
        setShowArchiveModal(false)
        setSelectedTenant(null)
        loadTenants()
      } else {
        const error = await response.text()
        alert(`Error archiving tenant: ${error}`)
      }
    } catch (error: any) {
      alert(`Error archiving tenant: ${error.message}`)
    }
  }

  const handleDeleteTenant = (tenant: Tenant) => {
    setSelectedTenant(tenant)
    setShowDeleteModal(true)
  }

  const deleteTenant = async () => {
    if (!selectedTenant) return

    try {
      const response = await fetch(`/api/admin/tenants/${selectedTenant.id}`, {
        method: 'DELETE',
        credentials: 'include'
      })

      if (response.ok) {
        const data = await response.json()
        setDeletionCertificate(data.certificate)
        setSelectedTenant(null)
        loadTenants()
      } else {
        const error = await response.text()
        alert(`Error deleting tenant: ${error}`)
        setShowDeleteModal(false)
        setSelectedTenant(null)
      }
    } catch (error: any) {
      alert(`Error deleting tenant: ${error.message}`)
      setShowDeleteModal(false)
      setSelectedTenant(null)
    }
  }

  const downloadDeletionCertificate = () => {
    if (!deletionCertificate) return

    const blob = new Blob([deletionCertificate], { type: 'text/plain' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `data-deletion-certificate-${selectedTenant?.domain}-${new Date().toISOString()}.txt`
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    URL.revokeObjectURL(url)

    setDeletionCertificate(null)
    setShowDeleteModal(false)
  }

  const handleRestoreTenant = (tenant: Tenant) => {
    setSelectedTenant(tenant)
    setShowRestoreModal(true)
  }

  const restoreTenant = async () => {
    if (!selectedTenant) return

    try {
      const response = await fetch(`/api/admin/tenants/${selectedTenant.id}/restore`, {
        method: 'POST',
        credentials: 'include'
      })

      if (response.ok) {
        setShowRestoreModal(false)
        setSelectedTenant(null)
        // Reload the appropriate list
        if (selectedTenant.status === 'archived') {
          loadArchivedTenants()
        } else if (selectedTenant.isDeleted) {
          loadDeletedTenants()
        }
        loadTenants() // Also reload active tenants
      } else {
        const error = await response.text()
        alert(`Error restoring tenant: ${error}`)
      }
    } catch (error: any) {
      alert(`Error restoring tenant: ${error.message}`)
    }
  }

  // Loading state
  if (authLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Checking authentication...</p>
        </div>
      </div>
    )
  }

  // Not authenticated - show login
  if (!isAuthenticated) {
    return <PlatformAdminLogin onLoginSuccess={checkAuth} />
  }

  // Authenticated - show admin interface
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            {/* Logo and Title */}
            <div className="flex items-center gap-3">
              <div className="p-2 bg-gradient-to-br from-blue-600 to-purple-600 rounded-lg">
                <Shield className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Platform Admin</h1>
                <p className="text-xs text-gray-600">Trust Center Management</p>
              </div>
            </div>

            {/* User Menu */}
            <div className="relative">
              <button
                onClick={() => setShowUserMenu(!showUserMenu)}
                className="flex items-center gap-3 px-4 py-2 bg-gray-50 hover:bg-gray-100 rounded-lg transition"
              >
                <div className="text-right">
                  <p className="text-sm font-medium text-gray-900">{user?.name || user?.email}</p>
                  <p className="text-xs text-gray-500">{user?.provider}</p>
                </div>
                <User className="w-5 h-5 text-gray-600" />
              </button>

              {showUserMenu && (
                <div className="absolute right-0 mt-2 w-64 bg-white rounded-lg shadow-lg border border-gray-200 py-2 z-50">
                  <div className="px-4 py-3 border-b border-gray-200">
                    <p className="text-sm font-medium text-gray-900">{user?.email}</p>
                    <p className="text-xs text-gray-500 mt-1">
                      Session expires: {user?.expiresAt ? new Date(user.expiresAt).toLocaleString() : 'N/A'}
                    </p>
                  </div>
                  <button
                    onClick={handleLogout}
                    className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50 flex items-center gap-2"
                  >
                    <LogOut className="w-4 h-4" />
                    Logout
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Navigation Tabs */}
          <div className="flex gap-2 mt-4 overflow-x-auto">
            <button
              onClick={() => setActiveTab('dashboard')}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition whitespace-nowrap ${
                activeTab === 'dashboard'
                  ? 'bg-blue-100 text-blue-700'
                  : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              <Shield className="w-4 h-4" />
              Dashboard
            </button>
            <button
              onClick={() => setActiveTab('tenants')}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition whitespace-nowrap ${
                activeTab === 'tenants'
                  ? 'bg-blue-100 text-blue-700'
                  : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              <Building2 className="w-4 h-4" />
              Tenants
            </button>
            <button
              onClick={() => setActiveTab('archive')}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition whitespace-nowrap ${
                activeTab === 'archive'
                  ? 'bg-blue-100 text-blue-700'
                  : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              <Archive className="w-4 h-4" />
              Archive
            </button>
            <button
              onClick={() => setActiveTab('recycle')}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition whitespace-nowrap ${
                activeTab === 'recycle'
                  ? 'bg-blue-100 text-blue-700'
                  : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              <Trash2 className="w-4 h-4" />
              Recycle Bin
            </button>
            <button
              onClick={() => setActiveTab('keys')}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition whitespace-nowrap ${
                activeTab === 'keys'
                  ? 'bg-blue-100 text-blue-700'
                  : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              <Key className="w-4 h-4" />
              Platform Keys
            </button>
            <button
              onClick={() => setActiveTab('analytics')}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition whitespace-nowrap ${
                activeTab === 'analytics'
                  ? 'bg-blue-100 text-blue-700'
                  : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              <BarChart3 className="w-4 h-4" />
              Analytics
            </button>
            <button
              onClick={() => setActiveTab('audit')}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition whitespace-nowrap ${
                activeTab === 'audit'
                  ? 'bg-blue-100 text-blue-700'
                  : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              <Activity className="w-4 h-4" />
              Audit Logs
            </button>
            <button
              onClick={() => setActiveTab('ai-prompts')}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition whitespace-nowrap ${
                activeTab === 'ai-prompts'
                  ? 'bg-blue-100 text-blue-700'
                  : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              <MessageSquare className="w-4 h-4" />
              AI Prompts
            </button>
            <button
              onClick={() => setActiveTab('health')}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition whitespace-nowrap ${
                activeTab === 'health'
                  ? 'bg-blue-100 text-blue-700'
                  : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              <Server className="w-4 h-4" />
              System Health
            </button>
            <button
              onClick={() => setActiveTab('settings')}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition whitespace-nowrap ${
                activeTab === 'settings'
                  ? 'bg-blue-100 text-blue-700'
                  : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              <Settings className="w-4 h-4" />
              Platform Settings
            </button>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Dashboard Tab */}
        {activeTab === 'dashboard' && (
          <PlatformAdminDashboard onNavigate={(tab) => setActiveTab(tab as Tab)} />
        )}

        {/* Tenants Tab */}
        {activeTab === 'tenants' && (
          <div>
            {/* Tenants Header */}
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="text-2xl font-bold text-gray-900">Tenants</h2>
                <p className="text-sm text-gray-600 mt-1">Manage trust center tenants</p>
              </div>
              <button
                onClick={() => setShowCreateModal(true)}
                className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg flex items-center gap-2"
              >
                <Plus className="w-4 h-4" />
                Create Tenant
              </button>
            </div>

            {/* Tenants Grid */}
            {loading ? (
              <div className="text-center py-12">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
                <p className="text-gray-600 mt-4">Loading tenants...</p>
              </div>
            ) : tenants.length === 0 ? (
              <div className="text-center py-12 bg-white rounded-lg border border-gray-200">
                <Building2 className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-gray-900 mb-2">No tenants yet</h3>
                <p className="text-gray-600 mb-6">Create your first trust center tenant to get started</p>
                <button
                  onClick={() => setShowCreateModal(true)}
                  className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg inline-flex items-center gap-2"
                >
                  <Plus className="w-4 h-4" />
                  Create Tenant
                </button>
              </div>
            ) : (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {tenants.map((tenant) => (
                  <div
                    key={tenant.id}
                    className="bg-white rounded-lg border border-gray-200 p-6 hover:shadow-lg transition"
                  >
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex items-center gap-3">
                        {tenant.settings?.logo ? (
                          <img
                            src={tenant.settings.logo}
                            alt={tenant.name}
                            className="w-10 h-10 object-contain"
                          />
                        ) : (
                          <div className="w-10 h-10 bg-gray-200 rounded-lg flex items-center justify-center">
                            <Building2 className="w-6 h-6 text-gray-600" />
                          </div>
                        )}
                        <div>
                          <h3 className="font-bold text-gray-900">{tenant.name}</h3>
                          <p className="text-sm text-gray-600">{tenant.domain}</p>
                        </div>
                      </div>
                    </div>

                    <div className="space-y-2 mb-4">
                      <p className="text-xs text-gray-500">
                        Created: {new Date(tenant.createdAt).toLocaleDateString()}
                      </p>
                      {tenant.settings?.customDomain && (
                        <p className="text-xs text-gray-500">
                          Custom Domain: {tenant.settings.customDomain}
                        </p>
                      )}
                    </div>

                    {/* Tenant Metrics */}
                    {tenant.metrics && (
                      <div className="bg-gray-50 rounded-lg p-3 mb-4">
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                          <div className="text-center">
                            <div className="flex items-center justify-center gap-1 text-gray-600 mb-1">
                              <FileText className="w-4 h-4" />
                            </div>
                            <div className="text-xl font-bold text-gray-900">
                              {tenant.metrics.totalDocuments || 0}
                            </div>
                            <div className="text-xs text-gray-500">Documents</div>
                          </div>

                          <div className="text-center">
                            <div className="flex items-center justify-center gap-1 text-gray-600 mb-1">
                              <CheckCircle className="w-4 h-4" />
                            </div>
                            <div className="text-xl font-bold text-gray-900">
                              {tenant.metrics.ndaAcceptances || 0}
                            </div>
                            <div className="text-xs text-gray-500">NDA Acceptances</div>
                          </div>
                        </div>
                      </div>
                    )}

                    <div className="space-y-2">
                      <div className="flex gap-2">
                        {hasSite(tenant) ? (
                          <button
                            onClick={() => openLiveSite(tenant)}
                            className="flex-1 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg text-sm flex items-center justify-center gap-2"
                            title="View Live Trust Center"
                          >
                            <ExternalLink className="w-4 h-4" />
                            View Site
                          </button>
                        ) : (
                          <button
                            disabled
                            className="flex-1 bg-gray-100 text-gray-400 px-4 py-2 rounded-lg text-sm flex items-center justify-center gap-2 cursor-not-allowed"
                            title="Trust center not yet created"
                          >
                            <ExternalLink className="w-4 h-4" />
                            No Site Yet
                          </button>
                        )}
                        <button
                          onClick={() => handleArchiveTenant(tenant)}
                          className="bg-orange-100 hover:bg-orange-200 text-orange-700 px-4 py-2 rounded-lg text-sm flex items-center gap-2"
                          title="Archive Tenant"
                        >
                          <Archive className="w-4 h-4" />
                        </button>
                      </div>
                      <button
                        onClick={() => handleDeleteTenant(tenant)}
                        className="w-full bg-red-50 hover:bg-red-100 text-red-700 px-4 py-2 rounded-lg text-sm flex items-center justify-center gap-2 border border-red-200"
                      >
                        <Trash2 className="w-4 h-4" />
                        Delete Tenant
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Bulk Tenant Redeploy */}
            <div className="mt-8">
              <BulkRedeployPanel />
            </div>
          </div>
        )}

        {/* Archive Tab */}
        {activeTab === 'archive' && (
          <div>
            {/* Archive Header */}
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="text-2xl font-bold text-gray-900">Archived Tenants</h2>
                <p className="text-sm text-gray-600 mt-1">Archived tenants - data preserved, can be restored</p>
              </div>
            </div>

            {/* Archived Tenants Grid */}
            {loading ? (
              <div className="text-center py-12">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
                <p className="text-gray-600 mt-4">Loading archived tenants...</p>
              </div>
            ) : archivedTenants.length === 0 ? (
              <div className="text-center py-12 bg-white rounded-lg border border-gray-200">
                <Archive className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-gray-900 mb-2">No archived tenants</h3>
                <p className="text-gray-600">Archived tenants will appear here</p>
              </div>
            ) : (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {archivedTenants.map((tenant) => (
                  <div
                    key={tenant.id}
                    className="bg-white rounded-lg border border-orange-200 p-6 hover:shadow-lg transition"
                  >
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex items-center gap-3">
                        {tenant.settings?.logo ? (
                          <img
                            src={tenant.settings.logo}
                            alt={tenant.name}
                            className="w-10 h-10 object-contain opacity-60"
                          />
                        ) : (
                          <div className="w-10 h-10 bg-gray-200 rounded-lg flex items-center justify-center opacity-60">
                            <Building2 className="w-6 h-6 text-gray-600" />
                          </div>
                        )}
                        <div>
                          <h3 className="font-bold text-gray-900">{tenant.name}</h3>
                          <p className="text-sm text-gray-600">{tenant.domain}</p>
                        </div>
                      </div>
                      <span className="px-2 py-1 bg-orange-100 text-orange-700 text-xs font-medium rounded">
                        Archived
                      </span>
                    </div>

                    <div className="space-y-2 mb-4">
                      <p className="text-xs text-gray-500">
                        Created: {new Date(tenant.createdAt).toLocaleDateString()}
                      </p>
                      <p className="text-xs text-gray-500">
                        Archived: {new Date(tenant.updatedAt).toLocaleDateString()}
                      </p>
                    </div>

                    <div className="space-y-2">
                      <button
                        onClick={() => handleRestoreTenant(tenant)}
                        className="w-full bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg text-sm flex items-center justify-center gap-2"
                      >
                        <RotateCcw className="w-4 h-4" />
                        Restore Tenant
                      </button>
                      <button
                        onClick={() => handleDeleteTenant(tenant)}
                        className="w-full bg-red-50 hover:bg-red-100 text-red-700 px-4 py-2 rounded-lg text-sm flex items-center justify-center gap-2 border border-red-200"
                      >
                        <Trash2 className="w-4 h-4" />
                        Permanently Delete
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Recycle Bin Tab */}
        {activeTab === 'recycle' && (
          <div>
            {/* Recycle Bin Header */}
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="text-2xl font-bold text-gray-900">Recycle Bin</h2>
                <p className="text-sm text-gray-600 mt-1">Deleted tenants - can be restored or permanently deleted</p>
              </div>
            </div>

            {/* Warning Banner */}
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
              <div className="flex items-start gap-3">
                <AlertTriangle className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-yellow-900">Tenants in Recycle Bin</p>
                  <p className="text-sm text-yellow-700 mt-1">
                    These tenants have been deleted but can still be restored. Permanent deletion removes all data irreversibly.
                  </p>
                </div>
              </div>
            </div>

            {/* Deleted Tenants Grid */}
            {loading ? (
              <div className="text-center py-12">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
                <p className="text-gray-600 mt-4">Loading deleted tenants...</p>
              </div>
            ) : deletedTenants.length === 0 ? (
              <div className="text-center py-12 bg-white rounded-lg border border-gray-200">
                <Trash2 className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Recycle bin is empty</h3>
                <p className="text-gray-600">Deleted tenants will appear here before permanent deletion</p>
              </div>
            ) : (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {deletedTenants.map((tenant) => (
                  <div
                    key={tenant.id}
                    className="bg-white rounded-lg border border-red-200 p-6 hover:shadow-lg transition"
                  >
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex items-center gap-3">
                        {tenant.settings?.logo ? (
                          <img
                            src={tenant.settings.logo}
                            alt={tenant.name}
                            className="w-10 h-10 object-contain opacity-40"
                          />
                        ) : (
                          <div className="w-10 h-10 bg-gray-200 rounded-lg flex items-center justify-center opacity-40">
                            <Building2 className="w-6 h-6 text-gray-600" />
                          </div>
                        )}
                        <div>
                          <h3 className="font-bold text-gray-900">{tenant.name}</h3>
                          <p className="text-sm text-gray-600">{tenant.domain}</p>
                        </div>
                      </div>
                      <span className="px-2 py-1 bg-red-100 text-red-700 text-xs font-medium rounded">
                        Deleted
                      </span>
                    </div>

                    <div className="space-y-2 mb-4">
                      <p className="text-xs text-gray-500">
                        Created: {new Date(tenant.createdAt).toLocaleDateString()}
                      </p>
                      {tenant.deletedAt && (
                        <p className="text-xs text-gray-500">
                          Deleted: {new Date(tenant.deletedAt).toLocaleDateString()}
                        </p>
                      )}
                    </div>

                    <div className="space-y-2">
                      <button
                        onClick={() => handleRestoreTenant(tenant)}
                        className="w-full bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg text-sm flex items-center justify-center gap-2"
                      >
                        <RotateCcw className="w-4 h-4" />
                        Restore Tenant
                      </button>
                      <button
                        onClick={() => handleDeleteTenant(tenant)}
                        className="w-full bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg text-sm flex items-center justify-center gap-2"
                      >
                        <Trash2 className="w-4 h-4" />
                        Permanently Delete
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Platform Keys Tab */}
        {activeTab === 'keys' && <PlatformKeysManager />}

        {/* Analytics Tab */}
        {activeTab === 'analytics' && <PlatformAnalytics />}

        {/* Audit Logs Tab */}
        {activeTab === 'audit' && <AuditLogViewer />}

        {/* AI Prompts Tab */}
        {activeTab === 'ai-prompts' && <AIPromptAnalytics />}

        {/* System Health Tab */}
        {activeTab === 'health' && <SystemHealthMonitor />}

        {/* Platform Settings Tab */}
        {activeTab === 'settings' && <PlatformSettings />}
      </div>

      {/* Create Tenant Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" onClick={() => setShowCreateModal(false)}>
          <div className="bg-white rounded-lg p-8 max-w-md w-full mx-4" onClick={(e) => e.stopPropagation()}>
            <h2 className="text-2xl font-bold text-gray-900 mb-6">Create New Tenant</h2>

            <div className="space-y-4 mb-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Company Name
                </label>
                <input
                  type="text"
                  value={newTenant.name}
                  onChange={(e) => setNewTenant({ ...newTenant, name: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Acme Corporation"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Domain
                </label>
                <input
                  type="text"
                  value={newTenant.domain}
                  onChange={(e) => setNewTenant({ ...newTenant, domain: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="acme.com"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Used for email allowlisting and tenant identification
                </p>
              </div>
            </div>

            <div className="flex gap-3">
              <button
                onClick={createTenant}
                className="flex-1 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg font-medium"
              >
                Create Tenant
              </button>
              <button
                onClick={() => {
                  setShowCreateModal(false)
                  setNewTenant({ name: '', domain: '' })
                }}
                className="flex-1 bg-gray-100 hover:bg-gray-200 text-gray-700 px-4 py-2 rounded-lg font-medium"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Archive Tenant Modal */}
      {showArchiveModal && selectedTenant && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" onClick={() => setShowArchiveModal(false)}>
          <div className="bg-white rounded-lg p-8 max-w-md w-full mx-4" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center gap-3 mb-4">
              <div className="p-3 bg-orange-100 rounded-full">
                <Archive className="w-6 h-6 text-orange-600" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900">Archive Tenant</h2>
            </div>

            <p className="text-gray-700 mb-6">
              Are you sure you want to archive <strong>{selectedTenant.name}</strong>?
              The tenant will be marked as inactive and hidden from the active tenants list,
              but all data will be preserved and can be restored later.
            </p>

            <div className="flex gap-3">
              <button
                onClick={archiveTenant}
                className="flex-1 bg-orange-600 hover:bg-orange-700 text-white px-4 py-2 rounded-lg font-medium"
              >
                Archive Tenant
              </button>
              <button
                onClick={() => {
                  setShowArchiveModal(false)
                  setSelectedTenant(null)
                }}
                className="flex-1 bg-gray-100 hover:bg-gray-200 text-gray-700 px-4 py-2 rounded-lg font-medium"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Tenant Modal */}
      {showDeleteModal && selectedTenant && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" onClick={() => !deletionCertificate && setShowDeleteModal(false)}>
          <div className="bg-white rounded-lg p-8 max-w-md w-full mx-4" onClick={(e) => e.stopPropagation()}>
            {!deletionCertificate ? (
              <>
                <div className="flex items-center gap-3 mb-4">
                  <div className="p-3 bg-red-100 rounded-full">
                    <AlertTriangle className="w-6 h-6 text-red-600" />
                  </div>
                  <h2 className="text-2xl font-bold text-gray-900">Delete Tenant</h2>
                </div>

                <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
                  <p className="text-sm text-red-800 font-semibold mb-2">⚠️ Warning: This action cannot be undone!</p>
                  <p className="text-sm text-red-700 mb-3">
                    All data associated with <strong>{selectedTenant.name}</strong> will be permanently deleted:
                  </p>

                  {selectedTenant.metrics ? (
                    <div className="bg-white border border-red-300 rounded p-3">
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm mb-3">
                        <div className="flex items-center justify-between">
                          <span className="text-gray-600">Documents:</span>
                          <span className="font-semibold text-red-700 text-lg">
                            {selectedTenant.metrics.totalDocuments || 0}
                          </span>
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="text-gray-600">NDA Acceptances:</span>
                          <span className="font-semibold text-red-700 text-lg">
                            {selectedTenant.metrics.ndaAcceptances || 0}
                          </span>
                        </div>
                      </div>
                      <div className="pt-2 border-t border-red-200">
                        <p className="text-xs text-red-600 italic">
                          Plus all user accounts, settings, configurations, audit logs, and access history
                        </p>
                      </div>
                    </div>
                  ) : (
                    <ul className="text-sm text-red-700 list-disc list-inside mt-2 space-y-1">
                      <li>All documents and files</li>
                      <li>User accounts and access logs</li>
                      <li>Settings and configurations</li>
                      <li>Audit trails and analytics</li>
                    </ul>
                  )}
                </div>

                <p className="text-gray-700 mb-6">
                  After deletion, you will receive a <strong>Data Deletion Certificate</strong> that
                  guarantees all customer data has been permanently removed from our systems.
                  This certificate can be provided to the customer as proof of data deletion.
                </p>

                <div className="flex gap-3">
                  <button
                    onClick={deleteTenant}
                    className="flex-1 bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg font-medium"
                  >
                    Permanently Delete
                  </button>
                  <button
                    onClick={() => {
                      setShowDeleteModal(false)
                      setSelectedTenant(null)
                    }}
                    className="flex-1 bg-gray-100 hover:bg-gray-200 text-gray-700 px-4 py-2 rounded-lg font-medium"
                  >
                    Cancel
                  </button>
                </div>
              </>
            ) : (
              <>
                <div className="flex items-center gap-3 mb-4">
                  <div className="p-3 bg-green-100 rounded-full">
                    <Download className="w-6 h-6 text-green-600" />
                  </div>
                  <h2 className="text-2xl font-bold text-gray-900">Data Deletion Certificate</h2>
                </div>

                <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
                  <p className="text-sm text-green-800 font-semibold mb-2">✓ Tenant Successfully Deleted</p>
                  <p className="text-sm text-green-700">
                    All data for <strong>{selectedTenant?.name}</strong> has been permanently deleted from our systems.
                  </p>
                </div>

                <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 mb-6 font-mono text-xs whitespace-pre-wrap max-h-48 overflow-y-auto">
                  {deletionCertificate}
                </div>

                <p className="text-sm text-gray-600 mb-6">
                  Download this certificate and provide it to the customer as proof that their data
                  has been permanently deleted in compliance with data protection regulations.
                </p>

                <button
                  onClick={downloadDeletionCertificate}
                  className="w-full bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg font-medium flex items-center justify-center gap-2"
                >
                  <Download className="w-4 h-4" />
                  Download Certificate
                </button>
              </>
            )}
          </div>
        </div>
      )}

      {/* Restore Tenant Modal */}
      {showRestoreModal && selectedTenant && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" onClick={() => setShowRestoreModal(false)}>
          <div className="bg-white rounded-lg p-8 max-w-md w-full mx-4" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center gap-3 mb-4">
              <div className="p-3 bg-green-100 rounded-full">
                <RotateCcw className="w-6 h-6 text-green-600" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900">Restore Tenant</h2>
            </div>

            <p className="text-gray-700 mb-6">
              Are you sure you want to restore <strong>{selectedTenant.name}</strong>?
              The tenant will be reactivated and moved back to the active tenants list with all data preserved.
            </p>

            <div className="flex gap-3">
              <button
                onClick={restoreTenant}
                className="flex-1 bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg font-medium"
              >
                Restore Tenant
              </button>
              <button
                onClick={() => {
                  setShowRestoreModal(false)
                  setSelectedTenant(null)
                }}
                className="flex-1 bg-gray-100 hover:bg-gray-200 text-gray-700 px-4 py-2 rounded-lg font-medium"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Click outside to close user menu */}
      {showUserMenu && (
        <div
          className="fixed inset-0 z-40"
          onClick={() => setShowUserMenu(false)}
        />
      )}
    </div>
  )
}

export default PlatformAdmin
